<script setup lang="ts">
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { computed, onMounted, Ref, ref } from 'vue';
    import { Code } from '@/shared/helpers/api/types/entities.ts';
    import { getCode, getLibrary, getScopes, sqlQuery } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper';
    import Spinner from '@/shared/components/Spinner.vue';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import { codeEditPermissionsForLibrary } from '@/shared/directives/requirePermission';
    import MultiScopeSelector from '@/shared/components/scopeselector/MultiScopeSelector.vue';

    type Props = {
        codeIdentity: string;
        codeset: string;
        library: string;
    };

    type Scope = {
        identity: string;
        description: string;
        codesetName: string;
    };

    const props = defineProps<Props>();

    const emit = defineEmits<{
        onCancel: [void];
        onDone: [void];
    }>();

    const loading: Ref<boolean> = ref(false);
    const hasChosenScopes: Ref<boolean> = ref<boolean>(false);
    const selectedScopes: Ref<string[]> = ref<string[]>([]);

    const codeToCopy: Ref<Code | null> = ref<Code | null>(null); // for creating the template
    const scopeList: Ref<Scope[]> = ref<Scope[]>([]); // to fill out the scope-selector
    const scopesWithExistingCodes: Ref<string[]> = ref<string[]>([]); // to block copying to codesets with an existing code

    onMounted(async () => {
        loading.value = true;
        // This fetches all necessary data for the component to do its job.
        // Should be replaced with a single endpoint similar to CodeEdit which gives everything needed in a single call
        const permissionsPromise = codeEditPermissionsForLibrary('LCIProject');
        const libraryPromise = getLibrary(http, props.library, { includeCodeSets: true });
        const codePromise = getCode(http, props.codeset, props.codeIdentity);
        const existingCodesPromise = sqlQuery(http, {
            query: `SELECT Scope FROM ${props.library} WHERE [Identity] = @Identity`,
            arguments: { Identity: props.codeIdentity }
        });
        const library = await libraryPromise;
        if (typeof library !== 'string') {
            const scopePromise = getScopes(http, library.scopeType);
            const [permissions, code, scopes, existingCodes] = await Promise.all([
                permissionsPromise,
                codePromise,
                scopePromise,
                existingCodesPromise
            ]);
            if (Array.isArray(scopes)) {
                codeToCopy.value = code;

                scopeList.value = library.codeSets.flatMap(cs =>
                    cs.scopes.map(s => {
                        return {
                            identity: s,
                            description: scopes.find(sc => sc.identity === s)?.description ?? '',
                            codesetName: cs.name };
                    })).filter(scope => permissions[scope.identity]);

                scopesWithExistingCodes.value = existingCodes.data.map(x => x.Scope as string);

                loading.value = false;
            }
        }
    });

    const filteredScopeList = computed(() => {
        return scopeList.value.filter(sc => !scopesWithExistingCodes.value.includes(sc.identity));
    });

    const codeSetsToRelease: Ref<string[]> = ref<string[]>([]);
    function onChooseScopes(): void {
        codeSetsToRelease.value = selectedScopes.value.map(ident => scopeList.value.find(sc => sc.identity === ident)!.codesetName);
        hasChosenScopes.value = true;
    }

    const codeTemplate = computed(() => {
        if (codeToCopy.value) {
            const template: { [attribute: string]: string } = {
                name: codeToCopy.value.name,
                description: codeToCopy.value.description
            };
            codeToCopy.value.attributes.forEach((attribute) => {
                template[attribute.definitionName] = attribute.displayValue;
            });
            return template;
        }
        return {};
    });

    const title = computed(() => {
        if (!hasChosenScopes.value) {
            return 'Select facilities';
        } else {
            return 'Create projects for ' + selectedScopes.value.toSorted().join(', ');
        }
    });

</script>

<template>
    <modal-base
        @on-cancel="emit('onCancel')">
        <template #modal-header>
            <h2 class="modal-title">
                {{ title }}
            </h2>
        </template>
        <template #modal-body>
            <spinner
                :loading="loading"
                :fullscreen="false">
                <div v-if="!hasChosenScopes">
                    <multi-scope-selector
                        v-if="filteredScopeList.length > 0"
                        v-model:selected-scopes="selectedScopes"
                        :scopes="filteredScopeList"
                        :disabled-scopes="scopesWithExistingCodes" />
                    <div v-else>
                        Item already exists for all facilities you have edit access to.
                    </div>
                </div>
                <div v-else>
                    <code-edit
                        :use-box-render="false"
                        :library="library"
                        :code-template-values="codeTemplate"
                        :fields-to-disable="['name']"
                        :code-set-name="codeSetsToRelease[0]"
                        :multiple-code-sets-to-release="codeSetsToRelease"
                        @cancel="emit('onCancel')"
                        @refresh="emit('onDone')" /><!-- For now, we always disable the name, but for other libraries fields that are part of the identity should be disabled-->
                </div>
            </spinner>
        </template>
        <template #modal-footer>
            <div class="is-pulled-right">
                <b-button
                    v-if="!hasChosenScopes"
                    icon-right="close-circle"
                    @click="emit('onCancel')">
                    Cancel
                </b-button>
                <b-button
                    v-if="!hasChosenScopes"
                    icon-right="arrow-right"
                    :disabled="selectedScopes.length === 0"
                    type="is-primary"
                    @click="onChooseScopes">
                    Next
                </b-button>
            </div>
        </template>
    </modal-base>
</template>

<style scoped>
    .modal-title {
        display: inline-block;
        max-width: 600px;
    }
</style>
